/**
 * LoadingFallback — route-aware skeleton fallback for Suspense boundaries.
 *
 * Renders a skeleton that matches the target page's layout so users see a
 * contextual placeholder instead of a blank white flash. Uses a subtle opacity
 * pulse (matching the NetworkQualityPage gold standard) rather than the horizontal
 * shimmer sweep — calmer, less distracting on route transitions.
 *
 * ponytail: co-located CSS (LoadingFallback.css) uses canonical tokens so dark
 * mode works automatically. One file per concern.
 */
import { useLocation, type Location } from 'react-router-dom';
import { useContext } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import './LoadingFallback.css';

/** Safe wrapper: returns pathname or '/' if no Router context exists. */
function usePathnameSafe(): string {
  const ctx = useContext(UNSAFE_LocationContext);
  if (!ctx) return '/';
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location: Location = useLocation();
  return location.pathname;
}

/** Determine skeleton variant from the current URL path. */
function getSkeletonVariant(pathname: string): 'search' | 'station' | 'observations' | 'observations-map' | 'availability' | 'table' {
  if (pathname.startsWith('/search')) return 'search';
  if (/^\/station\/[^/]+\/observations/.test(pathname)) return 'observations';
  if (pathname.startsWith('/station')) return 'station';
  if (pathname.startsWith('/observations')) return 'observations-map';
  if (pathname.startsWith('/availability')) return 'availability';
  return 'table';
}

/** Search page: map area + centered floating search bar */
function SearchSkeleton() {
  return (
    <div className="lf-search">
      {/* Floating search bar — centered, same position as SearchPage */}
      <div className="lf-search__bar-wrap">
        <div className="lf-bone lf-search__bar" />
      </div>
      {/* Map area placeholder */}
      <div className="lf-search__map" />
    </div>
  );
}

/** Station page: masthead + search + quick-access rail + detail panel */
function StationSkeleton() {
  return (
    <div className="lf-station">
      {/* Page masthead: title + subtitle */}
      <div className="lf-station__masthead">
        <div className="lf-bone" style={{ width: '30%', height: 22 }} />
        <div className="lf-bone" style={{ width: '55%', height: 13 }} />
      </div>
      {/* Search bar */}
      <div className="lf-bone lf-station__search" />
      {/* Grid: rail + focal (matches station-board__grid layout) */}
      <div className="lf-station__grid">
        {/* Quick-access rail (left) */}
        <div className="lf-station__rail">
          <div className="lf-bone" style={{ width: '45%', height: 12 }} />
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="lf-bone lf-station__rail-card" style={{ animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
        {/* Focal area (right) — detail panel placeholder */}
        <div className="lf-station__focal" />
      </div>
    </div>
  );
}

/** Observations page (/station/:code/observations): header + filters + data table */
function ObservationsSkeleton() {
  return (
    <div className="lf-observations">
      {/* Header row: station pill + name */}
      <div className="lf-observations__header">
        <div className="lf-bone lf-observations__pill" />
        <div className="lf-bone" style={{ width: '40%', height: 14 }} />
      </div>
      {/* Filter row: date pickers + view toggle */}
      <div className="lf-observations__filters">
        <div className="lf-bone" style={{ width: 130, height: 32 }} />
        <div className="lf-bone" style={{ width: 130, height: 32 }} />
        <div className="lf-bone" style={{ width: 100, height: 32, marginLeft: 'auto' }} />
      </div>
      {/* Actions row */}
      <div className="lf-observations__actions">
        <div className="lf-bone" style={{ width: 90, height: 24 }} />
        <div className="lf-bone" style={{ width: 70, height: 24, marginLeft: 'auto' }} />
      </div>
      {/* Table card */}
      <div className="lf-observations__table">
        {/* Table header */}
        <div className="lf-observations__row lf-observations__row--head">
          <div className="lf-bone" style={{ width: 80, height: 11 }} />
          {Array.from({ length: 5 }, (_, j) => (
            <div key={j} className="lf-bone" style={{ width: 50 + j * 8, height: 11 }} />
          ))}
        </div>
        {/* Table body */}
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="lf-observations__row" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="lf-bone" style={{ width: 120, height: 11 }} />
            {Array.from({ length: 5 }, (_, j) => (
              <div key={j} className="lf-bone" style={{ width: 30 + j * 6, height: 11 }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Observations map page: full map canvas + floating controls */
function ObservationsMapSkeleton() {
  return (
    <div className="lf-obs-map">
      {/* Top-left controls (parameter selector + network filter) */}
      <div className="lf-obs-map__controls">
        <div className="lf-bone" style={{ width: 180, height: 34, borderRadius: 17 }} />
        <div className="lf-bone" style={{ width: 140, height: 30, borderRadius: 15 }} />
      </div>
      {/* Bottom timeline bar */}
      <div className="lf-obs-map__timeline">
        <div className="lf-bone" style={{ width: '100%', height: 44, borderRadius: 12 }} />
      </div>
    </div>
  );
}

/** Availability page: header + search panel + result cards */
function AvailabilitySkeleton() {
  return (
    <div className="lf-availability">
      {/* Header: title + subtitle (matching page-view__header) */}
      <div className="lf-availability__header">
        <div className="lf-bone" style={{ width: '28%', height: 22 }} />
        <div className="lf-bone" style={{ width: '52%', height: 13 }} />
      </div>
      {/* Search panel: station input + parameter input + date inputs + button */}
      <div className="lf-availability__search">
        <div className="lf-bone" style={{ width: '100%', height: 38 }} />
        <div className="lf-availability__search-row">
          <div className="lf-bone" style={{ flex: 1, height: 38 }} />
          <div className="lf-bone" style={{ width: 130, height: 38 }} />
          <div className="lf-bone" style={{ width: 130, height: 38 }} />
          <div className="lf-bone" style={{ width: 90, height: 38, borderRadius: 19 }} />
        </div>
      </div>
      {/* Tabs (Station | Parameter) */}
      <div className="lf-availability__tabs">
        <div className="lf-bone" style={{ width: 70, height: 28, borderRadius: 6 }} />
        <div className="lf-bone" style={{ width: 80, height: 28, borderRadius: 6 }} />
      </div>
      {/* Result card placeholders */}
      {Array.from({ length: 2 }, (_, i) => (
        <div key={i} className="lf-availability__card" style={{ animationDelay: `${i * 100}ms` }}>
          <div className="lf-availability__card-header">
            <div className="lf-bone" style={{ width: 60, height: 13 }} />
            <div className="lf-bone" style={{ width: 120, height: 11 }} />
          </div>
          <div className="lf-availability__card-body">
            {Array.from({ length: 3 }, (_, r) => (
              <div key={r} className="lf-availability__card-row">
                <div className="lf-bone" style={{ width: 60, height: 12 }} />
                {Array.from({ length: 12 }, (_, c) => (
                  <div key={c} className="lf-bone lf-availability__cell" style={{ animationDelay: `${c * 30}ms` }} />
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Network / Quality / Statistics: header + filter + table */
function TablePageSkeleton() {
  return (
    <div className="lf-table">
      {/* Page header (title + subtitle) */}
      <div className="lf-table__header">
        <div className="lf-bone" style={{ width: '25%', height: 22 }} />
        <div className="lf-bone" style={{ width: '45%', height: 13 }} />
      </div>
      {/* Filter bar */}
      <div className="lf-table__toolbar">
        <div className="lf-bone" style={{ width: 200, height: 34, borderRadius: 17 }} />
        <div className="lf-bone" style={{ width: 90, height: 28 }} />
      </div>
      {/* Table surface */}
      <div className="lf-table__surface">
        {/* Table header row */}
        <div className="lf-table__row lf-table__row--head">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="lf-bone" style={{ width: `${50 + i * 5}%`, height: 11 }} />
          ))}
        </div>
        {/* Table body rows */}
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="lf-table__row" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="lf-bone" style={{ width: 90, height: 13 }} />
            <div className="lf-bone" style={{ width: 40, height: 13 }} />
            <div className="lf-bone" style={{ width: 40, height: 13 }} />
            <div className="lf-bone" style={{ width: 55, height: 13 }} />
            <div className="lf-bone" style={{ width: 50, height: 13 }} />
            <div className="lf-bone" style={{ width: 35, height: 13 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function LoadingFallback() {
  const pathname = usePathnameSafe();
  const variant = getSkeletonVariant(pathname);

  return (
    <div className="lf-root" role="status" aria-label="Loading page">
      {variant === 'search' && <SearchSkeleton />}
      {variant === 'station' && <StationSkeleton />}
      {variant === 'observations' && <ObservationsSkeleton />}
      {variant === 'observations-map' && <ObservationsMapSkeleton />}
      {variant === 'availability' && <AvailabilitySkeleton />}
      {variant === 'table' && <TablePageSkeleton />}
    </div>
  );
}
